// MACHINERY V2 (Stroje) — the rendered spec sheet: toolbar (collapse-all +
// metric/imperial), description blocks, grouped sections (M2.8).
//
// >>> WHY THIS FILE EXISTS: THE §2.3 SPEC-STACK DEVIATION. <<<
// The M2.8 ticket row says the catalog detail should "reuse `MachineSpecs`".
// It must NOT. v1's components/MachineSpecs.tsx imports MUI at :2
// (`Box, CircularProgress, Tooltip, useMediaQuery`), THIRTEEN MUI icons at
// :3-16 and `dt` at :20 — pulling it into v2 would breach §3's styling law in
// one import. The instruction's PURPOSE was "do not reinvent spec display",
// and the MUI-free v2 spec stack (specResolve / specSheet / SpecSectionsV2 /
// specChrome / SpecDescription, all shipped at M2.5) satisfies that purpose
// better than its letter.
//
// This is the RENDER half of SpecificationsTabV2, lifted out verbatim so the
// profile tab and the catalog detail page draw the SAME sheet from the SAME
// code rather than two copies that drift. The tab keeps everything that is
// tab-specific — the de-duplicated fetch, the shared-slot id guard, the error
// state — and hands the resolved sheet here.
//
// STATE THAT MOVED WITH THE RENDER: `metric` and the `collapsed` exception set
// live here, because both are pure view state that only this markup reads.
// Sections start EXPANDED, so the set tracks the EXCEPTIONS, not the members —
// no effect, no initialisation race, and a newly arrived section is open like
// the rest. Callers reset it by remounting (`key={modelId}`), which is also how
// the tab used to reset it when the machine changed.
//
// EQUIPMENT SECTIONS KEEP THEIR OWN EXPAND STATE and are deliberately NOT
// driven by Expand-All (data package §3 "Actions"); that is `EquipmentSection`'s
// own `useState` and is unchanged by this extraction.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useState } from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { ResolvedSpecSheet } from "./specResolve";
import { getSectionGroupKey } from "./specSheet";
import { GROUP_LABEL_KEYS, GroupDivider } from "./specChrome";
import { SpecSheetToolbar } from "./specSheetToolbar";
import { EquipmentSection, SpecSection } from "./SpecSectionsV2";
import { SpecDescription } from "./SpecDescription";

export const specSheetHasContent = (sheet: ResolvedSpecSheet): boolean =>
  sheet.primaryText.length > 0 ||
  sheet.machineTypeDescriptions.length > 0 ||
  sheet.regularSections.length > 0 ||
  sheet.equipmentSections.length > 0;

export interface SpecSheetViewProps {
  sheet: ResolvedSpecSheet;
  /** Mono eyebrow on the toolbar's left — the tab's intro line, or the
   *  catalog detail's "ŠPECIFIKÁCIE" label (artboard 0902). */
  label: string;
  /**
   * Rendered BETWEEN the toolbar and the sheet body: the retry notice, the
   * loading skeleton, the "no specifications" panel. A slot rather than props,
   * because the two hosts have genuinely different states to report (the tab
   * fetches into a SHARED slice slot and must guard the id; the catalog detail
   * owns its model outright) and neither should have to describe the other's.
   * The toolbar renders either way, so the metric toggle never disappears
   * under a transient state.
   */
  notices?: React.ReactNode;
  /** Dense section rendering below 768. Passed by ALL THREE hosts, unchanged. */
  phone?: boolean;
  /**
   * >>> "THIS SHEET IS RENDERING BELOW 1280." <<< The ">= 44px both axes" law,
   * which is a property of the VIEWPORT and of no particular host. Passed by
   * every host that can render below 1280 — SpecificationsTabV2 and
   * catalog/CatalogDetailMobile.tsx; catalog/CatalogDetailV2.tsx mounts this
   * sheet directly only at >= 1280 and correctly omits it.
   *
   * >>> IT USED TO MEAN "INSIDE THE PROFILE", AND THAT CONFLATION WAS A BUG
   * (M2.16e). <<< It was introduced at M2.16d review F7 to keep board 23's
   * toolbar REORDER off the two katalóg surfaces, and it did that job — but it
   * also carried the segment's touch target, so the only host that passed it
   * was the profile, and the katalóg's mobile detail rendered a ~28px
   * metric/imperial segment beside a collapse-all the module-wide `.btn` floor
   * had already raised to 44, in the same row. Two controls, one row, two
   * target sizes. The two meanings are now two props: `touch` is the viewport
   * law and every below-1280 host passes it; `profileHost` below is the
   * host-scoped half, and it alone gates the reorder.
   */
  touch?: boolean;
  /**
   * >>> "THIS SHEET IS THE PROFILE'S ŠPECIFIKÁCIE TAB." <<< Set ONLY by
   * SpecificationsTabV2. It gates board 23's phone toolbar order, which is a
   * PROFILE change: the two katalóg hosts were reviewed and signed off at
   * M2.16c and must keep artboard 1006's order at every width. If the reorder
   * turns out to be right for katalóg too, that is a katalóg-scoped change
   * with its own review, not a rider on a profile item.
   */
  profileHost?: boolean;
}

const SpecSheetView: React.FC<SpecSheetViewProps> = ({
  sheet,
  label,
  notices,
  phone,
  touch,
  profileHost,
}) => {
  const { translate } = useTranslation("myMachinery");
  const [metric, setMetric] = useState(true);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const hasContent = specSheetHasContent(sheet);

  const isOpen = (name: string) => !collapsed.has(name);
  const toggle = (name: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  const allExpanded = collapsed.size === 0;
  const toggleAll = () =>
    setCollapsed(
      allExpanded ? new Set(sheet.regularSections.map((s) => s.sectionName)) : new Set()
    );

  /** One divider per group, at its first section — v1's `renderedGroups`. */
  const rendered = new Set<string>();
  const dividerFor = (sectionName: string) => {
    const key = getSectionGroupKey(sectionName);
    if (!key || rendered.has(key)) return null;
    rendered.add(key);
    return <GroupDivider label={translate(GROUP_LABEL_KEYS[key])} groupKey={key} />;
  };

  return (
    <React.Fragment>
      {/* The toolbar's pixels and its two orders live in specSheetToolbar.tsx —
          see it for board 23 and for why the collapse-all button is now ONE
          node placed in two rows rather than two near-identical copies under
          two guards.

          `phoneOrder` is `profileHost && phone`, NOT `phone` and no longer
          `touch && phone`: the reorder is a PROFILE change and the two katalóg
          hosts must keep artboard 1006's order at every width, while `touch`
          is the viewport's 44px law and every below-1280 host now passes it.
          See both props' docblocks above. */}
      <SpecSheetToolbar
        label={label}
        metric={metric}
        onMetric={setMetric}
        allExpanded={allExpanded}
        onToggleAll={toggleAll}
        canCollapseAll={hasContent && sheet.regularSections.length > 0}
        phoneOrder={!!profileHost && !!phone}
        touch={touch}
      />

      {notices}

      {hasContent && (
        <React.Fragment>
          {sheet.primaryText && (
            <SpecDescription
              label={translate("myMachinery:specDescription")}
              text={sheet.primaryText}
              showEncodingFallback={sheet.usedEncodingFallback}
            />
          )}
          {sheet.machineTypeDescriptions.map((d) => (
            <SpecDescription key={d.label} label={d.label} text={d.text} />
          ))}

          {sheet.regularSections.map((section) => (
            <React.Fragment key={section.sectionName}>
              {dividerFor(section.sectionName)}
              <SpecSection
                section={section}
                open={isOpen(section.sectionName)}
                onToggle={() => toggle(section.sectionName)}
                toMetric={metric}
                phone={phone}
              />
            </React.Fragment>
          ))}

          {sheet.equipmentSections.length > 0 &&
            dividerFor(sheet.equipmentSections[0].sectionName)}
          {sheet.equipmentSections.map((section) => (
            <EquipmentSection key={section.sectionName} section={section} phone={phone} />
          ))}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default SpecSheetView;
